import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import { DiagStackAvailableOTXSequence } from '../../../projects/client';
import { AppState } from '../app.state';
import { DiagStackDtcSelectors } from '../diag-stack-dtc/diag-stack-dtc.selectors';
import { VddDetailsListSelectors } from '../vdd-details-list/vdd-details-list.selectors';
import { VddDetailsListState } from '../vdd-details-list/vdd-details-list.state';

import {
  DtcVdds,
  OtxBackupConfigurationResponseStructure,
  OtxProcedureType,
} from './diag-stack-otx.model';
import { DiagStackOtxState } from './diag-stack-otx.state';

export namespace DiagStackOtxSelectors {
  const diagStackOtx = (state: AppState): DiagStackOtxState => state.diagStackOtx;

  export const getOtxSequenceError = createSelector(
    diagStackOtx,
    (state: DiagStackOtxState): any => state.otxGetDTCErrorSettingConditionError
  );

  export const isOtxSequenceRunning = createSelector(
    diagStackOtx,
    (state: DiagStackOtxState): boolean => state.otxGetDTCErrorSettingConditionLoading
  );

  export const getOtxAvailableOtxSequencesError = createSelector(
    diagStackOtx,
    (state: DiagStackOtxState): any => state.availableOtxSequencesError
  );

  export const isOtxAvailableOtxSequencesLoading = createSelector(
    diagStackOtx,
    (state: DiagStackOtxState): boolean => state.availableOtxSequencesLoading
  );

  export const isOtxVddSequenceLoading = createSelector(
    isOtxSequenceRunning,
    isOtxAvailableOtxSequencesLoading,
    DiagStackDtcSelectors.isActiveDtcLoading,
    (
      isOtxSequenceRunningSelector: boolean,
      isOtxAvailableOtxSequencesLoadingSelector: boolean,
      isActiveDtcLoadingSelector: boolean
    ): boolean =>
      isOtxSequenceRunningSelector ||
      isOtxAvailableOtxSequencesLoadingSelector ||
      isActiveDtcLoadingSelector
  );

  export const isAnyOfTheOtxVddSequenceInError = createSelector(
    getOtxSequenceError,
    getOtxAvailableOtxSequencesError,
    DiagStackDtcSelectors.getActiveDtcError,
    (
      getOtxSequenceErrorSelector: boolean,
      getOtxAvailableOtxSequencesSelector: boolean,
      getActiveDtcErrorSelector: boolean
    ): boolean =>
      getOtxSequenceErrorSelector ||
      getOtxAvailableOtxSequencesSelector ||
      getActiveDtcErrorSelector
  );

  export const getDiagStackAvailableOtxSequences = createSelector(
    diagStackOtx,
    (state: DiagStackOtxState): DiagStackAvailableOTXSequence[] => state.availableOtxSequences
  );

  // only applicable for OTX sequence com.b2b.GetDTCErrorSettingCondition
  export const getListOfVddFromOtxSequenceResult = createSelector(
    diagStackOtx,
    (state: DiagStackOtxState): DtcVdds[] => {
      if (
        !state.otxGetDTCErrorSettingConditionResult ||
        state.otxGetDTCErrorSettingConditionResult.outResultStructure?.[0]?.type !==
          OtxProcedureType.DTCErrorSettingCondition
      ) {
        return [];
      }

      const dtcVddsEntries: DtcVdds[] = [];
      let currentDtcName: string = null;

      state.otxGetDTCErrorSettingConditionResult.outResultStructure.forEach((node) => {
        if (node.DTC) {
          currentDtcName = node.DTC;
          const dtcExist = dtcVddsEntries.find((entry) => entry.dtcName === node.DTC);
          if (!dtcExist) {
            const emptyDtc: DtcVdds = {
              dtcName: node.DTC,
              vddNames: [],
            };
            dtcVddsEntries.push(emptyDtc);
          }
        }
        if (node.VDD) {
          const dtcVddsEntry = dtcVddsEntries.find((entry) => entry.dtcName === currentDtcName);

          dtcVddsEntry.vddNames.push(node.VDD);
        }
      });

      return dtcVddsEntries;
    }
  );

  export const getFlattenedListOfVddFromOtxSequenceResult = createSelector(
    DiagStackOtxSelectors.getListOfVddFromOtxSequenceResult,
    (dtcVdds: DtcVdds[]): string[] => {
      let vddNames: string[] = [];
      dtcVdds.forEach((dtcVdd) => (vddNames = vddNames.concat(dtcVdd.vddNames)));

      return vddNames;
    }
  );

  export const isAnyFromOtxVddListLoading: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(
    DiagStackOtxSelectors.getFlattenedListOfVddFromOtxSequenceResult,
    VddDetailsListSelectors.VddDetailsList,
    (vddNames: string[], state: VddDetailsListState): boolean =>
      state.vddDetailsList
        .filter((vdd) => vddNames.includes(vdd.vddName))
        .findIndex(
          (vdd) => vdd?.metadataLoading || vdd?.structureLoading || vdd?.singleReadLoading
        ) !== -1
  );

  export const getBackupListOfResultStructures: MemoizedSelector<
    AppState,
    OtxBackupConfigurationResponseStructure[],
    DefaultProjectorFn<OtxBackupConfigurationResponseStructure[]>
  > = createSelector(
    diagStackOtx,
    (state: DiagStackOtxState): OtxBackupConfigurationResponseStructure[] =>
      state.otxBackupConfigurationResult?.backupListOfResultStructures
  );
}
